import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import type { Document, Manifest } from '../../api/authoring'
import { outlineOf } from '../../model/outline'

/**
 * The page's headings, as a reader's screen-reader shows them, and what
 * is wrong with the order.
 *
 * **A report, never a refusal.** A skipped level is a fact about a whole
 * page and the model validates one document at a time, so nothing can
 * refuse it at the write; and a page must not fail to render because its
 * headings are ranked badly. Telling the author is the whole of what is
 * available, and it is also the thing that was actually missing — the
 * platform could not previously see an outline at all, because every
 * heading was a tenant's own type.
 *
 * **It draws nothing when there is nothing to say.** A page with a clean
 * outline gets no panel: a permanent green tick is a thing an author
 * learns to stop reading, and the map is only interesting next to a
 * problem or when someone goes looking for it.
 */
export function PageOutline({
  page,
  manifest,
}: {
  page: Document
  manifest: Manifest | undefined
}) {
  const outline = outlineOf(page, manifest)
  if (outline.entries.length === 0 && outline.unchecked.length === 0) return null

  const faultsBy = new Map(outline.faults.map((fault) => [fault.linkId, fault]))

  return (
    <Paper>
      <Typography variant="h3" sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        Headings
      </Typography>

      <Stack spacing={1.5} sx={{ p: 2 }}>
        {outline.faults.length > 0 && (
          <Alert severity="warning" icon={<WarningAmberIcon />}>
            {outline.faults.length === 1
              ? 'One heading is out of order.'
              : `${outline.faults.length} headings are out of order.`}{' '}
            Screen readers and search engines read this order as the page's structure.
          </Alert>
        )}

        {outline.entries.map((entry) => {
          const fault = faultsBy.get(entry.linkId)
          return (
            <Box
              key={entry.linkId}
              sx={{ pl: Math.max(0, entry.level - 1) * 2, display: 'flex', gap: 1, alignItems: 'baseline' }}
            >
              <Chip
                size="small"
                variant={fault ? 'filled' : 'outlined'}
                color={fault ? 'warning' : 'default'}
                label={`H${entry.level || '?'}`}
              />
              <Typography variant="body2" sx={{ fontWeight: entry.level <= 1 ? 600 : 400 }}>
                {entry.text || <em>(no words)</em>}
              </Typography>
              {fault && (
                <Typography variant="caption" color="text.secondary">
                  {fault.detail}
                </Typography>
              )}
            </Box>
          )
        })}

        {/* Named rather than omitted: an outline that quietly left out
            half a page would be worse than one that says which half it
            could not see. A linked child's content is on its own stream
            and this page holds only the placement. */}
        {outline.unchecked.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {outline.unchecked.length === 1
              ? 'One block is shared with other pages, so its headings are not counted here.'
              : `${outline.unchecked.length} blocks are shared with other pages, so their headings are not counted here.`}
          </Typography>
        )}
      </Stack>
    </Paper>
  )
}
